import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from "react-router";
import { deleteApiWorkoutWorkoutid, getApiStatisticsProgram, getApiWorkoutList } from '../services/workout';
import MaterialTable, { Options, Query } from '@material-table/core';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import { urls, route } from '../route';
import { Workout } from '../models/Workout';
import { confirmAndDelete } from '../utils';
import { kind } from '../models/kind-model';
import { useSearchParams } from 'react-router-dom';

import icon from "../export-excel.png";
import moment from 'moment';
import { exportToExcel } from "../utils/exportExcel";

const StatusDisplay = {
    0: "Progress",
    1: "Done",
    2: "Waiting for re-schedule"
}
const download = async () => {
    const data = await getApiStatisticsProgram({});
    const rows = data.items.map((s) => ({
        email: s.email,
        programName: s.programName,
        phaseName: s.phaseName,
        score: s.score,
        startDate: s.startDate ? moment(s.startDate).format("L") : null,
        phaseStatus: StatusDisplay[s.phaseScheduleStatus],
        routineScheduleStatus: StatusDisplay[s.routineScheduleStatus],
        routineDate: s.routineDate ? moment(s.routineDate).format("L") : null,
    }));
    await exportToExcel("program-statistics", [
        {
            name: "Programs",
            data: rows,
            columns: [
                { label: "Email", value: "email" },
                { label: "Program", value: "programName" },
                { label: "Phase", value: "phaseName" },
                { label: "Assessment Score", value: "score" },
                { label: "Phase Start", value: "startDate" },
                { label: "Phase Status", value: "phaseStatus" },
                { label: "Routine Date", value: "routineDate" },
                { label: "Routine Status", value: "routineScheduleStatus" },
            ],
        },
    ]);
};

export const ProgramsList = () => {
    const history = useNavigate();
    const ref = useRef<any>();
    const [searchParams, setSearchParams] = useSearchParams()
    const [hidden, setHidden] = useState(false)

    const urlPage = Math.max(0, Number(searchParams.get("page") ?? 0) || 0)
    const urlPageSize = Math.max(1, Number(searchParams.get("pageSize") ?? 10) || 10)
    const urlSearch = searchParams.get("search") ?? ""
    const urlSortBy = (searchParams.get("sortBy") ?? "").trim() || undefined
    const urlSortOrder = (
        searchParams.get("sortOrder") === "asc"
            ? "asc"
            : searchParams.get("sortOrder") === "desc"
                ? "desc"
                : undefined
    ) as "asc" | "desc" | undefined

    const setUrlParams = useCallback((next: {
        page?: number
        pageSize?: number
        search?: string
        sortBy?: string
        sortOrder?: "asc" | "desc"
    }) => {
        const current = searchParams.toString()
        const sp = new URLSearchParams(searchParams)
        if (typeof next.page === "number") sp.set("page", String(Math.max(0, next.page)))
        if (typeof next.pageSize === "number") sp.set("pageSize", String(Math.max(1, next.pageSize)))
        if (typeof next.search === "string") {
            const v = next.search.trim()
            if (v) sp.set("search", v)
            else sp.delete("search")
        }
        if (typeof next.sortBy === "string") {
            const v = next.sortBy.trim()
            if (v) sp.set("sortBy", v)
            else sp.delete("sortBy")
        }
        if (next.sortOrder === "asc" || next.sortOrder === "desc") sp.set("sortOrder", next.sortOrder)
        else if (next.sortOrder === undefined) sp.delete("sortOrder")
        const nextStr = sp.toString()
        if (nextStr !== current) setSearchParams(sp, { replace: true })
    }, [searchParams, setSearchParams])

    const options = useMemo<Options<Workout>>(
        () => ({
            actionsColumnIndex: -1,
            selection: true,
            search: true,
            maxColumnSort: 1,
            pageSize: urlPageSize,
            initialPage: urlPage,
            searchText: urlSearch,
            columnsButton: true,
        }),
        [urlPage, urlPageSize, urlSearch]
    )

    const lastSearchRef = useRef<string>("")
    const debounceTimerRef = useRef<number | undefined>(undefined)
    const debounceMs = 300

    const dataLoader = useCallback(async (q: Query<Workout>) => {
        const search = (q.search ?? "").trim()
        const searchChanged = search !== lastSearchRef.current

        if (searchChanged) {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
            await new Promise<void>((r) => {
                debounceTimerRef.current = window.setTimeout(r, debounceMs) as unknown as number
            })
        }

        const pageIndex = searchChanged ? 0 : (q.page ?? 0)
        const pageSize = q.pageSize ?? 10
        const skip = pageIndex * pageSize

        const rawSortBy = q.orderBy?.field
        const sortBy = Array.isArray(rawSortBy) ? rawSortBy[0] : rawSortBy
        const sortOrder = q.orderDirection

        const allowedSortBy = new Set(["id", "name"])
        const safeSortBy = typeof sortBy === "string" && allowedSortBy.has(sortBy) ? sortBy : undefined

        setUrlParams({
            page: pageIndex,
            pageSize,
            search,
            sortBy: typeof safeSortBy === "string" ? safeSortBy : "",
            sortOrder,
        })

        const res = await getApiWorkoutList({
            kind: [kind.programs],
            includeHidden: hidden,
            skip,
            limit: pageSize,
            search,
            sortBy: safeSortBy,
            sortOrder,
        })
        lastSearchRef.current = search

        const totalCount =
            typeof res.total === "number"
                ? res.total
                : pageIndex * pageSize + res.items.length
        const allowedMaxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)
        const safePage = Math.min(pageIndex, allowedMaxPage)

        return { data: res.items, page: safePage, totalCount }
    }, [hidden])
    return <div>
        <MaterialTable<Workout>
            title="Programs"
            options={options}
            tableRef={ref}
            columns={[
                { title: "Id", field: "id", cellStyle: { width: "10%" } },
                { title: "Name", field: "name", cellStyle: { width: "25%" } },
                { title: "Type", field: "type", sorting: false, render: (e: Workout) => <><div>{e.type!.name}</div></>, cellStyle: { width: "25%" } },
                { title: "# of Phases", field: "childCount", sorting: false, cellStyle: { width: "15%" } },
                { title: "# of Routines", field: "grandChildCount", sorting: false, cellStyle: { width: "15%" } },
                {
                    title: 'Tools',
                    sorting: false,
                    render: (e) => (<>
                        <div style={{ float: "left" }}>
                            <div style={{ float: e.hidden ? "right" : "left", width: "50%", visibility: e.hidden ? "visible" : "hidden", backgroundColor: "#e6e6e6", textAlign: "center", fontWeight: "bold" }}>
                                <span style={{ writingMode: "vertical-rl", textOrientation: "upright" }}>HIDDEN</span>
                            </div>
                            <div style={{ float: e.hidden ? "left" : "right", width: "50%" }}>
                                <IconButton onClick={() => history(`/app/programs/edit/${e.id}`)} style={{ float: "right", top: 20 }} ><Icon>edit</Icon></IconButton>
                                <IconButton onClick={confirmAndDelete(e, deleteApiWorkoutWorkoutid, ref)} style={{ float: "right", top: 15 }}><Icon>delete</Icon></IconButton>
                            </div>
                        </div>
                    </>), cellStyle: { width: "5%" }
                }
            ]}
            data={dataLoader as any}
            actions={[{
                icon: "delete",
                tooltip: "Delete",
                onClick: () => { }
            }, {
                icon: "visibilityOff",
                tooltip: "View Hidden",
                isFreeAction: true,
                onClick: () => { }

            },
            {
                icon: "add",
                tooltip: "Add New",
                isFreeAction: true,
                onClick: () => history(route(urls.programsAdd)),
            },
            {
                tooltip: "Export CSV",
                isFreeAction: true,
                disabled: false,
                icon: "export-csv",
                onClick: download
            }]}
            components={{
                Action: props => {
                    if (props.action.icon === "export-csv") {
                        return <IconButton onClick={props.action.onClick} size="small">
                            <img alt='export-excel' src={String(icon)} style={{
                                width: "24px",
                                height: "24px",
                            }} />
                        </IconButton>
                    }
                    if (props.action.icon === "visibilityOff")
                        return <Checkbox inputProps={{ 'aria-label': 'uncontrolled-checkbox' }} value={hidden} checked={hidden}
                            onClick={() => {
                                setHidden(!hidden)
                                if (ref.current) {
                                    ref.current.onQueryChange();
                                }
                            }} />
                    else if (props.action.icon === "delete") {
                        const element = props.action;
                        return <IconButton aria-label={element.icon} size="small"
                            onClick={async () => {
                                const confirmation = window.confirm("Are you sure you want to delete the selected items?")
                                if (confirmation && Array.isArray(props.data)) {
                                    for (const i of props.data) {
                                        await deleteApiWorkoutWorkoutid(i.id);
                                    }
                                    if (ref.current) {
                                        ref.current.onQueryChange();
                                    }
                                }
                            }}>
                            <Icon>{element.icon}</Icon>
                        </IconButton>
                    } else {
                        const element = props.action;
                        return <IconButton aria-label={element.icon} size="small"
                            onClick={element.onClick}>
                            <Icon>{element.icon}</Icon>
                        </IconButton>
                    }
                }
            }}
        />
    </div>
}